import React, { useEffect, useState } from 'react';
import { CircularProgress, Grid, Typography } from '@mui/material';
import { getLocalEvents, LocalEvent } from 'core/API/local-events';
import './LocalEvents.scss';

interface LocalEventsProps {
	workplaceId?: number | null;
	className?: string;
	onEventClick?: (event: LocalEvent) => void;
	title?: string;
}

/**
 * Reusable component for displaying local events
 * Fetches events based on workplace ID
 */
export const LocalEvents: React.FC<LocalEventsProps> = ({ 
	workplaceId, 
	className = '', 
	onEventClick,
	title = 'Local Events'
}) => {
	const [localEvents, setLocalEvents] = useState<LocalEvent[]>([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		const fetchLocalEvents = async () => {
			if (!workplaceId) return;

			setLoading(true);
			try {
				const events = await getLocalEvents(workplaceId);
				setLocalEvents(events);
			} catch (error) {
				console.error('Error fetching local events:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchLocalEvents();
	}, [workplaceId]);

	return (
		<Grid container direction="column" className={`local-events-container white-card ${className}`}>
			{loading ? (
				<Grid item className="loading-container">
					<CircularProgress size={20} />
				</Grid>
			) : (
				<Grid item className="local-events-list scrollable">
					{localEvents.map((event) => (
						<Grid
							key={event.id}
							container
							item
							className="local-event-item"
							direction="column"
							spacing={1}
							onClick={() => onEventClick && onEventClick(event)}
							sx={{ cursor: onEventClick ? 'pointer' : 'default' }}
						>
							<Grid item>
								<Grid container className="event-title">
									{event.title}
								</Grid>
							</Grid>
							<Grid item>
								<Grid>
									{event.date?.slice(0, 10)} {event.time?.slice(0, 5)} {event.timezone}
								</Grid>
							</Grid>
							<Grid item style={{ padding: '0px' }}>
								<Grid
									container
									direction="column"
									className="event-details"
									spacing={1}
									alignItems="flex-start"
								>
									{event.why_hot && (
										<Grid item className="why-hot">
											{event.why_hot}
										</Grid>
									)}
									{(event.sport || event.broadcast) && (
										<Grid item className="broadcast">
											{event.sport || event.broadcast}
										</Grid>
									)}
									<Grid item className="event-location">
										{event.city}, {event.country}
									</Grid>
								</Grid>
							</Grid>
							<Grid item style={{ marginBottom: '5px' }}></Grid>
						</Grid>
					))}
					{localEvents.length === 0 && !loading && (
						<Grid item className="no-events">
							No local events available
						</Grid>
					)}
				</Grid>
			)}
		</Grid>
	);
};
